import React, { useState, useEffect } from 'react';
import { getTrustedDevices, revokeTrustedDevice, revokeAllTrustedDevices } from '../../../API/Api';
import { toast } from 'react-toastify';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';

const TrustedDevices = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRevokeDialog, setShowRevokeDialog] = useState(false);
  const [showRevokeAllDialog, setShowRevokeAllDialog] = useState(false);
  const [deviceToRevoke, setDeviceToRevoke] = useState(null);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    try {
      const response = await getTrustedDevices();
      // 只显示激活的设备（isActive: true）
      const activeDevices = (response.data.devices || []).filter(device => device.isActive !== false);
      setDevices(activeDevices);
    } catch (error) {
      toast.error('Failed to load trusted devices');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeClick = (deviceId) => {
    setDeviceToRevoke(deviceId);
    setShowRevokeDialog(true);
  };

  const handleRevokeConfirm = async () => {
    if (!deviceToRevoke) return;

    try {
      await revokeTrustedDevice(deviceToRevoke);
      toast.success('Device revoked successfully');
      // 从列表中移除该设备
      setDevices(devices.filter(device => device._id !== deviceToRevoke));
      setShowRevokeDialog(false);
      setDeviceToRevoke(null);
    } catch (error) {
      toast.error('Failed to revoke device');
      setShowRevokeDialog(false);
      setDeviceToRevoke(null);
    }
  };

  const handleRevokeAllClick = () => {
    setShowRevokeAllDialog(true);
  };

  const handleRevokeAllConfirm = async () => {
    try {
      await revokeAllTrustedDevices();
      toast.success('All devices revoked successfully');
      // 清空设备列表
      setDevices([]);
      setShowRevokeAllDialog(false);
    } catch (error) {
      toast.error('Failed to revoke devices');
      setShowRevokeAllDialog(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Trusted Devices</h2>
        {devices.length > 0 && (
          <button
            onClick={handleRevokeAllClick}
            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
          >
            Revoke All
          </button>
        )}
      </div>

      {devices.length === 0 ? (
        <p className="text-gray-500">No trusted devices</p>
      ) : (
        <div className="space-y-4">
          {devices.map((device) => (
            <div key={device._id} className="border rounded p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold">{device.deviceName || 'Unknown Device'}</h3>
                  <p className="text-sm text-gray-600">
                    {device.browserInfo?.browserName} {device.browserInfo?.browserVersion} on {device.browserInfo?.os}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Trusted: {formatDate(device.trustedAt)} |
                    Last used: {formatDate(device.lastUsedAt)} |
                    Expires: {formatDate(device.expiresAt)}
                  </p>
                  {device.ipAddress && (
                    <p className="text-xs text-gray-500">IP: {device.ipAddress}</p>
                  )}
                </div>
                <button
                  onClick={() => handleRevokeClick(device._id)}
                  className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition"
                >
                  Revoke
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Revoke Single Device Dialog */}
      <Dialog open={showRevokeDialog} onClose={() => setShowRevokeDialog(false)}>
        <div className='p-6 min-w-[400px]'>
          <div className='flex justify-between items-start mb-4'>
            <h2 className='text-lg font-semibold text-red-600'>Revoke Device</h2>
            <Button onClick={() => setShowRevokeDialog(false)} className='min-w-0 p-1 text-gray-500 hover:text-gray-700'>
              ×
            </Button>
          </div>
          <div className='mb-6'>
            <p className='text-gray-700 leading-relaxed'>
              Are you sure you want to revoke this device? You will need to verify with OTP on your next login from this device.
            </p>
          </div>
          <div className='flex justify-end gap-3'>
            <button
              onClick={() => setShowRevokeDialog(false)}
              className='px-6 py-2 border-2 border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded font-semibold transition-colors'
            >
              Cancel
            </button>
            <button
              onClick={handleRevokeConfirm}
              className='px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded font-semibold transition-colors'
            >
              Revoke
            </button>
          </div>
        </div>
      </Dialog>

      {/* Revoke All Devices Dialog */}
      <Dialog open={showRevokeAllDialog} onClose={() => setShowRevokeAllDialog(false)}>
        <div className='p-6 min-w-[400px]'>
          <div className='flex justify-between items-start mb-4'>
            <h2 className='text-lg font-semibold text-red-600'>Revoke All Devices</h2>
            <Button onClick={() => setShowRevokeAllDialog(false)} className='min-w-0 p-1 text-gray-500 hover:text-gray-700'>
              ×
            </Button>
          </div>
          <div className='mb-6'>
            <p className='text-gray-700 leading-relaxed'>
              Are you sure you want to revoke all trusted devices? You will need to verify with OTP on your next login from any device.
            </p>
          </div>
          <div className='flex justify-end gap-3'>
            <button
              onClick={() => setShowRevokeAllDialog(false)}
              className='px-6 py-2 border-2 border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded font-semibold transition-colors'
            >
              Cancel
            </button>
            <button
              onClick={handleRevokeAllConfirm}
              className='px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded font-semibold transition-colors'
            >
              Revoke All
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TrustedDevices;

